import {DriverActivityEntity, transportType} from "@ots-frontend/ots-data";
import {GridCellContextMenu} from "../../data-grid/DataGrid/useDataGrid";
import {Menu} from "@mantine/core";
import {TransportActionMenuDropdown} from "../../menu/TransportActionMenu/TransportActionMenu";
import {IconChartLine, IconEye, IconMap, IconPlus} from "@tabler/icons-react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useTicketFormPageNavigate} from "../../../pages/TicketFormPage/useTicketFormPageNavigate";
import {PersonActionMenuDropdown} from "../../menu/PersonActionMenu/PersonActionMenu";
import {GeofenceEventActionMenuDropdown} from "../../menu/GeofenceEventActionMenu/GeofenceEventActionMenu";
import {ShiftActionMenuDropdown} from "../../menu/ShiftActionMenu/ShiftActionMenu";
import {ALL_DISPATCH_ACTIONS, DispatchActionMenuDropdown} from "../../menu/DispatchActionMenu/DispatchActionMenu";
import {TicketActionMenuDropdown} from "../../menu/TicketActionMenu/TicketActionMenu";
import {DriverActivityTtaMenu} from "../DriverActivityTtaMenu/DriverActivityTtaMenu";
import {useMemo} from "react";

export type DriverActivityContextMenuProps = GridCellContextMenu<DriverActivityEntity> & {
  context?: DriverActivityEntity[]
}

export const DriverActivityContextMenu = (props: DriverActivityContextMenuProps) => {
  const {data, context, onItemClick} = props

  const {t} = useTranslation(["common"])
  const navigate = useNavigate()

  const {navigate: ticketFormNavigate} = useTicketFormPageNavigate()

  const shift = data.startShift ?? data.endShift
  const transport = data.pickupTransport ?? data.deliveryTransport

  const selections = useMemo(() => {
    const activityMap = new Map<string, DriverActivityEntity>();
    if (data.id && data.type) {
      const key = data.id + "_" + data.type
      activityMap.set(key, data)
    }

    if (context) {
      for (const activity of context) {
        if (activity.id && activity.type) {
          const key = activity.id + "_" + activity.type
          if (!activityMap.has(key)) {
            activityMap.set(key, activity)
          }
        }
      }
    }

    return [...activityMap.values()]
  }, [data, context])

  if (!data.person && !data.tta && !shift && !transport && !data.geofenceEvent && !data.dispatch) {
    return (
      <Menu shadow="md" width={200} opened={true}>
        <Menu.Dropdown>
          <Menu.Item>
            {t("common.action.none", "No Actions")}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    )
  }

  return (
    <Menu shadow="md" width={250} opened={true} closeOnItemClick={true} onClose={onItemClick}>
      <Menu.Dropdown>
        {data.person &&
          <>
            <Menu.Label>{data.person.nameReverse}</Menu.Label>
            <Menu.Item
              icon={<IconEye size={14}/>}
              onClick={() => navigate(`/persons/profile?id=${data.id}`)}>
              {t("common.action.view", "View")}
            </Menu.Item>
            <PersonActionMenuDropdown person={data.person}/>
          </>
        }
        {data.tta &&
          <>
            <Menu.Label>{t("common.entity.tta.alt", "Ticket") + " " + data.tta.resourceIdentifiers.join(", ")}</Menu.Label>
            <Menu.Item
              icon={<IconEye size={14}/>}
              onClick={() => navigate(`/tickets/profile?id=${data.id}&database=${data.tta?.database}`)}>
              {t("common.action.view", "View")}
            </Menu.Item>
            <Menu.Item
              icon={<IconMap size={14}/>}
              onClick={() => navigate(`/tickets/profile/map?id=${data.id}&database=${data.tta?.database}`)}>
              {t("common.action.map", "Map")}
            </Menu.Item>
            <Menu.Item
              icon={<IconChartLine size={14}/>}
              onClick={() => navigate(`/tickets/profile?id=${data.id}&database=${data.tta?.database}`, {state: {tabValue: 'scada'}})}>
              {t("common.action.scada", "Scada")}
            </Menu.Item>
            <TicketActionMenuDropdown ticket={data.tta}/>
          </>
        }
        {transport &&
          <>
            <Menu.Label>{t(transport.type.displayValue.id, transport.type.displayValue.defaultMessage) + " " + t("common.entity.transport", "Transport")}</Menu.Label>
            <Menu.Item
              icon={<IconPlus size={14}/>}
              onClick={() => ticketFormNavigate(transport)}>
              {t("common.action.add", "Add") + " " + (transport.type === transportType.pickup ? t(transportType.delivery.displayValue.id, transportType.delivery.displayValue.defaultMessage) : t(transportType.pickup.displayValue.id, transportType.pickup.displayValue.defaultMessage))}
            </Menu.Item>
            <TransportActionMenuDropdown transport={transport}/>
          </>
        }
        {data.dispatch &&
          <>
            <Menu.Label>{t("common.entity.dispatch", "Dispatch")}</Menu.Label>
            <Menu.Item
              icon={<IconEye size={14}/>}
              onClick={() => navigate(`/dispatch/profile?id=${data.id}`)}>
              {t("common.action.view", "View")}
            </Menu.Item>
            <DispatchActionMenuDropdown
              dispatch={data.dispatch}
              actions={["EDIT", "DELETE"]}
            />
          </>
        }
        {shift &&
          <>
            <Menu.Label>{t("common.entity.shift", "Shift")}</Menu.Label>
            <Menu.Item
              icon={<IconEye size={14}/>}
              onClick={() => navigate(`/shifts/profile?id=${data.id}`)}>
              {t("common.action.view", "View")}
            </Menu.Item>
            <ShiftActionMenuDropdown shift={shift}/>
          </>
        }
        {data.geofenceEvent &&
          <>
            <Menu.Label>{t("common.entity.geofenceevent", "Geofence Event")}</Menu.Label>
            <GeofenceEventActionMenuDropdown geofenceEvent={data.geofenceEvent}/>
          </>
        }
        <DriverActivityTtaMenu context={selections}/>
      </Menu.Dropdown>
    </Menu>
  )
}
